import { useState, useEffect, useRef, type RefObject } from 'react'

interface UseInViewOptions {
  /** Threshold for intersection (0-1) */
  threshold?: number
  /** Margin around the root */
  rootMargin?: string
  /** Only trigger once */
  triggerOnce?: boolean
}

/**
 * Hook to detect when an element is visible in the viewport
 * Uses IntersectionObserver for performance
 * 
 * Phase 3.2: Performance - Pause animations when off-screen
 */
export function useInView(
  ref: RefObject<Element>,
  options: UseInViewOptions = {}
): boolean {
  const { threshold = 0.1, rootMargin = '0px', triggerOnce = false } = options
  const [isInView, setIsInView] = useState(false)
  const hasTriggered = useRef(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    // If triggerOnce and already triggered, don't observe
    if (triggerOnce && hasTriggered.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return  // Type guard: entry may be undefined
        const inView = entry.isIntersecting
        setIsInView(inView)

        if (inView && triggerOnce) {
          hasTriggered.current = true
          observer.disconnect()
        }
      },
      { threshold, rootMargin }
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [ref, threshold, rootMargin, triggerOnce])

  return isInView
}

/**
 * Hook to track multiple elements' visibility
 */
export function useInViewMultiple(
  refs: RefObject<Element>[],
  options: UseInViewOptions = {}
): boolean[] {
  const { threshold = 0.1, rootMargin = '0px' } = options
  const [inViewStates, setInViewStates] = useState<boolean[]>(
    () => new Array(refs.length).fill(false)
  )

  useEffect(() => {
    const observers: IntersectionObserver[] = []

    refs.forEach((ref, index) => {
      const element = ref.current
      if (!element) return

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return  // Type guard: entry may be undefined
          setInViewStates((prev) => {
            const next = [...prev]
            next[index] = entry.isIntersecting
            return next
          })
        },
        { threshold, rootMargin }
      )

      observer.observe(element)
      observers.push(observer)
    })

    return () => {
      observers.forEach((observer) => observer.disconnect())
    }
  }, [refs, threshold, rootMargin])

  return inViewStates
}
